import { getAiPluginState } from "../plugins/ai-plugin";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TextSelection } from "prosemirror-state";
import { AiPopupState } from "../types";
import { htmlToNodes, markdownToNodes } from "../../../helpers/parsers";
import { Fragment } from "prosemirror-model";
import { findNodePosition } from "./helpers";
import { serializeToHTMLFromSchema } from "../../../helpers";
const getPopupState = (state, results, isAddInline) => {
    const docIsEmpty = state.doc.content.size === 2;
    const hasSelection = state.selection.from !== state.selection.to;
    if (docIsEmpty) {
        return AiPopupState.EmptyDocument;
    }
    if (isAddInline && hasSelection) {
        return AiPopupState.InlineContentGenerated;
    }
    if (results.length > 0 && hasSelection) {
        return AiPopupState.ContentGenerated;
    }
    if (hasSelection) {
        return AiPopupState.ContentSelected;
    }
    return AiPopupState.EmptySelection;
};
export function useAiPopup({ view, service, }) {
    const aiPluginState = getAiPluginState(view.state);
    const isVisible = aiPluginState === null || aiPluginState === void 0 ? void 0 : aiPluginState.visible;
    const [loadingResponse, setLoadingResponse] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const contentMode = (service === null || service === void 0 ? void 0 : service.contentMode) || "html";
    const navigateHistoryLeft = useCallback(() => {
        if (currentResultIndex > 0) {
            setCurrentResultIndex(currentResultIndex - 1);
        }
    }, [currentResultIndex, setCurrentResultIndex]);
    const navigateHistoryRight = useCallback(() => {
        if (currentResultIndex < results.length - 1) {
            setCurrentResultIndex(currentResultIndex + 1);
        }
    }, [currentResultIndex, setCurrentResultIndex, results.length]);
    const [isAddResultInline, setIsAddResultInline] = useState(false);
    const popupState = useMemo(() => {
        return getPopupState(view.state, results, isAddResultInline);
    }, [view.state, results, isAddResultInline]);
    const currentResult = useMemo(() => {
        return results === null || results === void 0 ? void 0 : results[currentResultIndex !== null && currentResultIndex !== void 0 ? currentResultIndex : 0];
    }, [results, currentResultIndex]);
    useEffect(() => {
        if (!isVisible) {
            setIsAddResultInline(false);
            setCurrentResultIndex(0);
            setError(null);
            setResults([]);
        }
    }, [isVisible]);
    const updateResult = useCallback((index, result) => {
        setResults((prevResults) => {
            const newResults = [...prevResults];
            newResults[index] = result;
            return newResults;
        });
    }, [setResults]);
    const handleInlinePrompt = useCallback((newResultIndex, from) => {
        let prevFragmentSize = 0;
        let fullResponse = "";
        // Add placeholder paragraph
        const initialTr = view.state.tr;
        initialTr.insert(from, view.state.schema.nodes.paragraph.create({}, [
            view.state.schema.text(" "),
        ]));
        // Store paragraph ref for getting the position later
        const node = initialTr.doc.nodeAt(from + 1);
        view.dispatch(initialTr);
        const callback = (word) => {
            fullResponse += word;
            // parse content using html or markdown parser
            const resultFragment = contentMode === "html"
                ? htmlToNodes(fullResponse, view.state.schema)
                : markdownToNodes(fullResponse, view.state.schema);
            const tr = view.state.tr;
            // position of the placeholder paragraph in the TR document
            let writingParagraphPos = findNodePosition(tr.doc, node);
            // delete previous fragment until the placeholder paragraph
            if (prevFragmentSize) {
                tr.delete(from, writingParagraphPos - 1);
            }
            // insert new fragment
            tr.insert(from, resultFragment);
            writingParagraphPos = findNodePosition(tr.doc, node);
            // set selection to the end of the fragment
            tr.setSelection(TextSelection.create(tr.doc, from, writingParagraphPos - 1));
            view.dispatch(tr);
            prevFragmentSize = resultFragment.size;
        };
        // when done, remove the placeholder paragraph and set the selection to the last fragment size.
        callback.then = () => {
            const tr = view.state.tr;
            const writingParagraphPos = findNodePosition(tr.doc, node);
            // tr.delete(writingParagraphPos, node.nodeSize);
            tr.delete(writingParagraphPos, writingParagraphPos + node.nodeSize);
            tr.setSelection(TextSelection.create(tr.doc, from, writingParagraphPos - 1));
            view.dispatch(tr);
        };
        return callback;
    }, [view, contentMode]);
    const handleHistoryPrompt = useCallback((newResultIndex) => {
        let fullResponse = "";
        updateResult(newResultIndex, {
            html: "",
            fragment: Fragment.empty,
            index: newResultIndex,
            fullResponse,
            doc: null,
        });
        return (word) => {
            fullResponse += word;
            const resultFragment = contentMode === "html"
                ? htmlToNodes(fullResponse, view.state.schema)
                : markdownToNodes(fullResponse, view.state.schema);
            updateResult(newResultIndex, {
                html: serializeToHTMLFromSchema(resultFragment, view.state.schema),
                fragment: resultFragment,
                fullResponse,
                index: newResultIndex,
                doc: view.state.schema.nodes.doc.create(null, resultFragment),
            });
        };
    }, [view, updateResult, contentMode]);
    const handlePrompt = useCallback((prompt) => {
        var _a;
        setLoadingResponse(true);
        setError(null);
        const shouldAddResultInline = [
            AiPopupState.EmptySelection,
            AiPopupState.EmptyDocument,
        ].includes(popupState);
        setIsAddResultInline(shouldAddResultInline);
        const newResultIndex = results.length;
        const from = view.state.selection.from;
        const callback = shouldAddResultInline
            ? handleInlinePrompt(newResultIndex, from)
            : handleHistoryPrompt(newResultIndex);
        setCurrentResultIndex(newResultIndex);
        service
            .stream(prompt, callback)
            .then((_a = callback === null || callback === void 0 ? void 0 : callback.then) !== null && _a !== void 0 ? _a : (() => null))
            .finally(() => {
            setLoadingResponse(false);
        })
            .catch((error) => {
            setError(error.message);
            setLoadingResponse(false);
            console.error(error);
        });
    }, [
        service,
        view,
        results,
        setIsAddResultInline,
        popupState,
        handleInlinePrompt,
        handleHistoryPrompt,
    ]);
    return {
        isVisible,
        popupState,
        loadingResponse,
        currentResult,
        error,
        handlePrompt,
        results,
        navigateHistoryLeft,
        navigateHistoryRight,
    };
}
